import { Component, Inject, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseModal } from '../../../../modals/base-modal';
import { ReportSettings } from '../reports.component';

type CompObj = {
  available: { id: string, name: string }[];
  selected: { id: string, name: string }[];
  options: { id: string, name: string }[];
  hidden: { id: string, name: string }[];
  valid: boolean;
  title: string;
}

@Component({
  selector: 'app-custom-report',
  templateUrl: './custom-report.component.html',
  styleUrls: ['./custom-report.component.scss'],
})
export class CustomReportComponent extends BaseModal implements OnInit {
  @ViewChild('reportName') reportTypeRef!: ElementRef<HTMLSelectElement>;

  private _settings: ReportSettings;
  private get hasChanges() {
    const reference = JSON.stringify(this._settings);
    const updates = JSON.stringify(this.settings);
    const hasChange = (reference !== updates);

    return hasChange;
  }

  public get editMode() { return (this.title.includes('Edit')) }
  public customers: { id: string, name: string }[] = [];
  public locations: { id: string, name: string }[] = [];
  public submitEnabled: boolean = false;
  public nameError: boolean = false;
  public recipients: string[] = [];
  public names: string[] = [];

  public settings: ReportSettings;
  public title: string;

  public levelOptions = {
    title: 'Report Type:',
    key: 'level',
    options: [
      { value: '0', label: 'Specific Customer(s)', id: 'customer' },
      { value: '1', label: 'Specific Location(s)', id: 'location' },
      { value: '2', label: 'Business Wide', id: 'bus-wide' },
      { value: '3', label: 'Custom', id: 'custom' }
    ]
  };

  public unitTypeOptions = {
    title: 'Unit Type:',
    key: 'unitType',
    options: [
      { value: '0', label: 'Days', id: 'days' },
      { value: '1', label: 'Weeks', id: 'weeks' },
      { value: '2', label: 'Months', id: 'months' },
      { value: '3', label: 'Day of Week', id: 'dow' },
      { value: '4', label: 'Day of Month', id: 'dom' },
      { value: '5', label: 'Daily', id: 'daily' }
    ]
  };

  public alertFilters = [
    {
      title: 'Device Events',
      events: [
        'Sensitivity Update',
        'Heartbeat',
        'Missed Heartbeat',
        'Low Battery',
        'Power',
        'Reset'
      ]
    },
    {
      title: 'Triggers',
      events: [
        'Reed',
        'PIR',
        'Vibration',
        'Voltage',
        'Power Source',
        'Temperature',
        'Humidity'
      ]
    },
    {
      title: 'State Changes',
      events: [
        'Magnet',
        'Water',
        'Pump',
        'Gate',
        'Tab'
      ]
    }
  ];

  constructor(
    protected dialogRef: MatDialogRef<CustomReportComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    super(dialogRef, data);

    let settingsTmp: ReportSettings = {
      times: ['', '', ''],
      recipients: [],
      timeOfDay: '',
      customers: [],
      locations: [],
      unitAmount: 1,
      filters: [],
      unitType: 0,
      level: 0,
      name: ''
    };

    if (data.settings) settingsTmp = { ...data.settings };
    if (!settingsTmp.times) settingsTmp.times = ['', '', ''];
    if (!settingsTmp.unitAmount) settingsTmp.unitAmount = 0;
    if (!settingsTmp.customers) settingsTmp.customers = [];
    if (!settingsTmp.locations) settingsTmp.locations = [];
    if (!settingsTmp.timeOfDay) settingsTmp.timeOfDay = '';

    while (settingsTmp.times.length > 3) settingsTmp.times.push('');
    this._settings = this.untetherObject(settingsTmp);
    this.settings = this.untetherObject(settingsTmp);

    this.recipients = [...data.recipients];
    this.customers = [...data.customers];
    this.locations = [...data.locations];
    this.names = [...data.names];
    this.title = `${data.title}`;
  }

  private validateForm() {
    let invalid = false;

    if (!this.hasChanges) {
      if (this.submitEnabled) this.submitEnabled = false;
      return;
    }

    if (this.settings.level === 4) {
      if (
        !this.settings.recipients.length ||
        !this.settings.times?.[0] ||
        !this.settings.times?.[1] ||
        !this.settings.times?.[2]
      ) invalid = true;
    } else {
      const hasNameError = (
        this.names.includes(this.settings.name) &&
        this.settings.name !== '' &&
        !this.editMode
      );

      if (
        (this.settings.unitType === 5 && !this.settings.times?.find(t => t !== '')) ||
        ([0, 3].includes(this.settings.level) && !this.settings.customers?.length) ||
        ([1, 3].includes(this.settings.level) && !this.settings.locations?.length) ||
        (this.settings.unitType !== 5 && !this.settings.timeOfDay) ||
        !this.settings.recipients.length ||
        !this.settings.filters.length ||
        this.settings.name === '' ||
        hasNameError
      ) invalid = true;

      if (this.nameError !== hasNameError) this.nameError = hasNameError;
    }

    if (this.submitEnabled !== !invalid) this.submitEnabled = !invalid;
  }

  private emailValid(id: string, email: string, comp: CompObj) {
    const valid = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
    comp.valid = (valid && ![...comp.selected, ...comp.available].find(i => i.id === id));
  }

  private updateArrays(items: { id: string, name: string }[], comp: CompObj, type: ('add' | 'rem')) {
    const title = comp.title.toLowerCase();
    const ids = items.map(i => i.id);

    if (type === 'rem') {
      (this.settings as any)[title] = (this.settings as any)[title].filter((i: any) => !ids.includes(i));
      comp.selected = comp.selected.filter((i: any) => !ids.includes(i.id));
      comp.available.push(...items);
    } else {
      comp.available = comp.available.filter((i: any) => !ids.includes(i.id));
      (this.settings as any)[title].push(...ids);
      comp.selected.push(...items);
    }
  }

  private untetherObject(obj: Object) {
    return JSON.parse(JSON.stringify(obj));
  }

  filterOptions(filter: HTMLInputElement, comp: CompObj, event: any) {
    const isPaste = (event.clipboardData !== undefined);
    const value = (isPaste ? event.clipboardData?.getData('text') : filter.value);
    const id = (filter.getAttribute('data') || '');
    const title = comp.title.toLowerCase();

    const all = [...comp.available, ...comp.hidden];
    const isRep = (comp.title === 'Recipients');
    const exact = all.find(i => i === value);

    comp.available = all.filter((i) => i.name.includes(value));
    comp.hidden = all.filter((i) => !i.name.includes(value));

    if (isRep) this.emailValid(id, value, comp);
    if (!isPaste && event.key === 'Enter' && (exact || (isRep && comp.valid))) {
      if (isRep && !exact) this.addRecipient(comp, filter);
      else {
        const item = (this as any)[title].find((i: any) => i.id === id);
        this.updateArrays([item], comp, 'add');
      }

      filter.value = '';
    }
  }

  moveSelection(opts: HTMLDivElement, comp: CompObj, type: ('add' | 'rem')) {
    const elms = Array.from(opts.querySelectorAll('[selected]'));
    const items = elms.map((e: any) => ({ name: e.innerText, id: e.getAttribute('data') }));

    elms.map((e) => e.removeAttribute('selected'));
    this.updateArrays(items, comp, type);
    this.validateForm();
  }

  updateSetting(elm: HTMLInputElement) {
    const type = (['radio', 'number'].includes(elm.getAttribute('type') as string) ? 'number' : 'string');
    const key = (elm.getAttribute('name') as string);
    let value = elm.value;

    if (key === 'unitAmount') {
      if (![3, 4].includes(this.settings.unitType) && (value === '' || Number(value) < 1)) value = '1';
      else if (this.settings.unitType === 4 && Number(value) > 31) value = '31';
      else if (this.settings.unitType === 3) {
        if (Number(value) < 0) value = '0';
        if (Number(value) > 6) value = '6';
      }

      if (elm.value !== value) elm.value = value;
    }

    if (!key.includes('time-')) (this.settings as any)[key] = (type === 'number' ? Number(value) : value);

    if (key === 'unitType'  && this.settings.unitAmount != null) {
      if (value === '4') {
        if (this.settings.unitAmount > 31) this.settings.unitAmount = 31;
        else if (!this.settings.unitAmount) this.settings.unitAmount = 1;
      } else if (value === '3') {
        if (this.settings.unitAmount > 6) this.settings.unitAmount = 6;
        else if (this.settings.unitAmount < 0) this.settings.unitAmount = 0;
      }
    } else if (key.includes('time-')) {
      const index = Number(key.split('-')[1]);
      (this.settings.times as any)[index] = value;
    }

    this.validateForm();
  }

  toggleSelected(event: MouseEvent, isFilter?: boolean) {
    if (!event.target) return;

    const target = (event.target as HTMLElement);
    const selected = (isFilter ? target.getAttribute('selected') === 'true' : target.hasAttribute('selected'));

    if (isFilter) {
      if (selected) this.settings.filters = this.settings.filters.filter(i => i !== target.innerText);
      else this.settings.filters.push(target.innerText);

      this.validateForm();
    } else {
      if (selected) target.removeAttribute('selected');
      else target.setAttribute('selected', '');
    }
  }

  addRecipient(comp: CompObj, filter: HTMLInputElement) {
    const id = (filter.getAttribute('data') || filter.value);
    const email = filter.value;

    this.updateArrays([{ name: email, id: id }], comp, 'add');
    this.emailValid(id, email, comp);
    this.validateForm();

    filter.value = '';
  }

  initComp(comp: CompObj) {
    if (comp.available !== undefined) return comp.title;
    const title = comp.title.toLowerCase();

    const items = ((this.settings as any)[title].length ? (this.settings as any)[title] : []);
    comp.selected = items.map((i: any) => comp.options.find((o: any) => o.id === i)).filter((i: any) => i !== undefined);
    comp.available = comp.options.filter(o => !comp.selected.find(s => s.id === o.id));
    comp.valid = false;
    comp.hidden = [];

    return comp.title;
  }

  ngOnInit(): void {
    this.showDialog();

    setTimeout(() => {
      if (this.editMode) {
        const element = this.reportTypeRef.nativeElement;
        element?.blur();
      }
    }, 150);
  }

  submit(): void {
    if (!this.hasChanges) return this.dialogRef.close('');

    let out: any = {
      unitType: Number(`${this.settings.unitType}`),
      recipients: [...this.settings.recipients],
      level: Number(`${this.settings.level}`),
      filters: [...this.settings.filters],
      name: `${this.settings.name}`
    };

    if ([0, 3].includes(out.level)) out.customers = [...(this.settings.customers || [])];
    if ([1, 3].includes(out.level)) out.locations = [...(this.settings.locations || [])];
    if (out.unitType === 5) {
      let tmp = [...(this.settings.times || [])];
      if (out.level !== 4) tmp.sort();

      out.times = [...tmp.filter(i => i !== '')];
    } else {
      out.unitAmount = Number(`${this.settings.unitAmount}`);
      out.timeOfDay = `${this.settings.timeOfDay}`;
    }

    this.dialogRef.close(out);
  }
}
